import { Box, Button, TextField, useTheme } from "@mui/material";
import * as THREE from "three";
import { WorldRenderer } from "../../WorldRenderer";
import { PropValueLine } from "../../common/PropValueLine";
import { BaseVisual, VisualEvents } from "../../../visuals/BaseVisual";
import { Vec3Editor } from "../../common/Vec3Editor";
import { Vec3 } from "../../../models/SyntheticMap";
import { useEffect, useState } from "react";
import { LightVisual } from "../../../visuals/LightVisual";
import { LightProps } from "./LightProps";
import { FloorVisual } from "../../../visuals/FloorVisual";
import { FloorProps } from "./FloorProps";
import { RobotVisual } from "../../../visuals/RobotVisual";
import { RobotProps } from "./RobotProps";
import { ArmVisual } from "../../../visuals/ArmVisual";
import { ArmProps } from "./ArmProps";
import { ModelBasedProps } from "./ModelBasedProps";
import { ModelBasedVisual } from "../../../visuals/ModelBasedVisual";
import { C4Visual } from "../../../visuals/C4Visual";
import { C4Props } from "./C4Props";
import { RenderAccordionToolbar } from "../../../components/Minibar";

export function VisualProps({
  wr,
  visual,
  pinned,
  panel,
  togglePin,
}: {
  wr: WorldRenderer;
  visual: BaseVisual;
  pinned: Set<string>;
  panel: string;
  togglePin: (panel: string) => void;
}) {
  const theme = useTheme();
  const [visualState, setVisualState] = useState<BaseVisual>(visual);
  useEffect(() => {
    setVisualState(visual);
    const updateVisualState = (newVisual: BaseVisual, source: string) => {
      if (source === "props") {
        return;
      }
      setVisualState(newVisual);
    };
    visual.on(VisualEvents.DataUpdated, updateVisualState);
  }, [visual]);

  const merge = (partial: Partial<BaseVisual>) => {
    const newVisual = { ...visualState, ...partial };
    setVisualState(newVisual as BaseVisual);
    visual.read(newVisual);
    visual.emit(VisualEvents.DataUpdated, newVisual, "props");
  };

  return (
    <>
      <RenderAccordionToolbar
        pinned={pinned}
        panel={panel}
        togglePin={togglePin}
      />
      <Box>
        {/* <PropValueLine label='DBID' value={visual._id} /> */}
        <PropValueLine
          label="ID"
          value={
            <TextField
              fullWidth
              value={visual.id}
              variant="standard"
              size="small"
              onChange={(e) => {
                merge({ id: e.target.value });
              }}
            />
          }
        />

        <PropValueLine
          label="sysID"
          value={`${visual._id}:${visual.getType()}`}
        />
        <PropValueLine
          label="Position"
          value={
            <Vec3Editor
              value={visualState.position}
              onChange={(v: Vec3) => {
                merge({ position: v });
              }}
              onSelect={() => {
                wr.userEventer.positionSelectAction = (
                  position: THREE.Vector3
                ) => {
                  merge({ position });
                };
              }}
            />
          }
        />
        <PropValueLine
          label="Rotation"
          value={
            <Vec3Editor
              convertToDegrees
              value={visualState.rotation}
              onChange={(v: Vec3) => {
                merge({ rotation: v });
              }}
            />
          }
        />
        {visual instanceof ModelBasedVisual && (
          <ModelBasedProps wr={wr} modelBased={visual} onUpdate={merge} />
        )}
        {visual instanceof LightVisual && (
          <LightProps wr={wr} light={visual} onUpdate={merge} />
        )}
        {visual instanceof FloorVisual && (
          <FloorProps wr={wr} floor={visual} onUpdate={merge} />
        )}
        {visual instanceof RobotVisual && (
          <RobotProps wr={wr} robot={visual} onUpdate={merge} />
        )}
        {visual instanceof ArmVisual && (
          <ArmProps wr={wr} arm={visual} onUpdate={merge} />
        )}
        {visual instanceof C4Visual && (
          <C4Props wr={wr} c4={visual} onUpdate={merge} />
        )}
        <Box>
          <Button
            color="secondary"
            onClick={async () => {
              await visual.save();
            }}
          >
            Save
          </Button>

          <Button
            color="secondary"
            onClick={async () => {
              visual.focusCamera();
            }}
          >
            Focus Camera
          </Button>

          <Button
            color="secondary"
            onClick={async () => {
              const activeSite = wr.dataService.getActiveSite();
              await activeSite.removeVisual(visual);
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </>
  );
}
